class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
    this.handleResize = this.handleResize.bind(this);
    this.mutationObserver = null;
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
    window.addEventListener('resize', this.handleResize);
    this.setupMutationObserver();
    // Init Swiper if content already in DOM (e.g. section moved into tab slot or server-rendered)
    this._initSwiperWhenReady();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    if (this._visibilityObserver) {
      this._visibilityObserver.disconnect();
    }
  }

  handleResize() {
    // Reinitialize swiper on resize if needed
    this.initializeSwiper();
  }

  setupMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if swiper container was added
          const swiperContainer = this.querySelector('.related-products-slider');
          if (swiperContainer && !swiperContainer.swiper) {
            this.initializeSwiper();
          }
        }
      });
    });

    this.mutationObserver.observe(this, {
      childList: true,
      subtree: true
    });
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    if (this.dataset.noFetch === 'true') {
      this.initializeSwiper();
      return;
    }
    const sectionId = this.dataset.sectionId || (
      this.dataset.useRelatedSection === 'true' &&
      document.querySelector('product-recommendations[data-related-products-section-id]')?.dataset?.relatedProductsSectionId
    ) || '';
    if (!this.dataset.url || !sectionId) return;
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }

        this.initializeSwiper();
      })
      .catch((e) => {
        console.error(e);
      });
  }

  _initSwiperWhenReady() {
    const self = this;
    function tryInit() {
      if (self.querySelector('.related-products-slider') && window.Swiper) {
        self.initializeSwiper();
      }
    }
    if (document.readyState === 'complete') {
      tryInit();
    } else {
      window.addEventListener('load', tryInit);
    }
    requestAnimationFrame(tryInit);
  }

  initializeSwiper() {
    if (!window.Swiper) return;

    const swiperContainer = this.querySelector('.related-products-slider');
    if (!swiperContainer || swiperContainer.swiper) return;

    // PDP tab block: 2 slides per view on mobile, 4 on desktop (also when slot exists but section not yet moved)
    const isInSlot = !!this.closest('.related-products-wrapper[data-related-products-slot]');
    const slotExists = !!document.querySelector('.related-products-wrapper[data-related-products-slot]');
    const isPdpTabBlock = isInSlot || (slotExists && this.classList.contains('related-products'));
    const isMobile = window.innerWidth < 750;

    if (isPdpTabBlock) {
      const paginationEl = swiperContainer.querySelector('.swiper-pagination');
      const nextEl = swiperContainer.querySelector('.swiper-button-next.swiper-button-pdp');
      const prevEl = swiperContainer.querySelector('.swiper-button-prev.swiper-button-pdp');
      const pdpSwiper = new Swiper(swiperContainer, {
        slidesPerView: 2,
        spaceBetween: 16,
        loop: false,
        watchOverflow: true,
        pagination: paginationEl ? {
          el: paginationEl,
          clickable: true,
        } : false,
        navigation: (nextEl && prevEl) ? {
          nextEl: nextEl,
          prevEl: prevEl,
        } : false,
        allowTouchMove: true,
        breakpoints: {
          0: {
            slidesPerView: 2,
            spaceBetween: 16,
          },
          750: {
            slidesPerView: 4,
            spaceBetween: 20,
          },
        },
        on: {
          init: function() {
            const el = swiperContainer.closest('product-recommendations');
            if (el && el._observeSliderVisibility) el._observeSliderVisibility(swiperContainer, this);
          },
        },
      });
      swiperContainer.swiper = pdpSwiper;
      return;
    }

    // Default: mobile-only slider (e.g. standalone related-products section)
    if (!isMobile) return;

    const paginationElDefault = swiperContainer.querySelector('.swiper-pagination');
    const swiper = new Swiper(swiperContainer, {
      slidesPerView: 1.3,
      spaceBetween: 15,
      centeredSlides: false,
      loop: false,
      freeMode: false,
      pagination: paginationElDefault ? {
        el: paginationElDefault,
        clickable: true,
      } : false,
      allowTouchMove: true,
      breakpoints: {
        0: {
          slidesPerView: 1.3,
          spaceBetween: 15,
        },
        480: {
          slidesPerView: 1.5,
          spaceBetween: 15,
        },
      },
    });
    swiperContainer.swiper = swiper;
  }

  _observeSliderVisibility(swiperContainer, swiperInstance) {
    if (!swiperInstance || this._visibilityObserver) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && swiperContainer.swiper && typeof swiperContainer.swiper.update === 'function') {
          swiperContainer.swiper.update();
        }
      },
      { rootMargin: '0px', threshold: 0.01 }
    );
    observer.observe(swiperContainer);
    this._visibilityObserver = observer;
  }
}

customElements.define('product-recommendations', ProductRecommendations);
